import { isUnauthorizedError } from "@shared/lib/errors";

export const AUTH_ROUTE_PREFIXES = [
  "/login",
  "/register",
  "/verify-email",
  "/signup",
  "/onboarding",
  "/logout",
  "/forgot-password",
  "/reset-password",
];

const DEFAULT_AUTHENTICATED_ROUTE = "/dashboard";

type RouterLocationLike = {
  href?: string | null;
  pathname?: string | null;
  searchStr?: string | null;
  hash?: string | null;
};

export type InternalRouteTarget = {
  pathname: string;
  search: Record<string, string>;
  hash?: string;
};

export type LoginRedirectSearch = {
  reason: string;
  returnTo?: string;
};

function getOrigin(origin?: string) {
  if (origin) return origin;
  if (typeof window !== "undefined" && window.location?.origin)
    return window.location.origin;
  return "http://localhost";
}

function normalizePathname(pathname?: string | null) {
  const value = pathname?.length ? pathname : "/";
  return value.startsWith("/") ? value : `/${value}`;
}

export function isAuthRoutePath(pathname?: string | null) {
  const normalized = normalizePathname(pathname).toLowerCase();
  return AUTH_ROUTE_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}

export function getRouteHref(location?: RouterLocationLike | null) {
  const href =
    typeof location?.href === "string" && location.href ? location.href : null;
  if (href) return href;

  const pathname = normalizePathname(location?.pathname);
  const search = location?.searchStr || "";
  const hash = location?.hash || "";
  return `${pathname}${search}${hash}`;
}

export function resolveInternalRedirectTarget(
  rawTarget?: string | null,
  fallback = DEFAULT_AUTHENTICATED_ROUTE,
  origin?: string,
) {
  if (!rawTarget) return fallback;

  const trimmed = rawTarget.trim();
  if (!trimmed) return fallback;

  const baseOrigin = getOrigin(origin);
  const isHttpAbsolute = /^https?:\/\//i.test(trimmed);
  const isRootRelative = trimmed.startsWith("/") && !trimmed.startsWith("//");
  if (!isHttpAbsolute && !isRootRelative) return fallback;

  try {
    const parsed = new URL(trimmed, baseOrigin);
    if (parsed.origin !== baseOrigin) return fallback;
    if (isAuthRoutePath(parsed.pathname)) return fallback;
    if (parsed.searchParams.has("returnTo")) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || fallback;
  } catch {
    if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return fallback;
    if (isAuthRoutePath(trimmed)) return fallback;
    if (trimmed.includes("returnTo=")) return fallback;
    return trimmed;
  }
}

export function getLoginReturnTo(location?: RouterLocationLike | null) {
  const target = resolveInternalRedirectTarget(
    getRouteHref(location),
    DEFAULT_AUTHENTICATED_ROUTE,
  );
  return target === DEFAULT_AUTHENTICATED_ROUTE &&
    isAuthRoutePath(location?.pathname)
    ? undefined
    : target;
}

export function createLoginRedirectSearch(
  location: RouterLocationLike | null | undefined,
  reason = "auth-required",
): LoginRedirectSearch {
  const returnTo = getLoginReturnTo(location);
  return returnTo ? { reason, returnTo } : { reason };
}

export function splitInternalRedirectTarget(
  target: string,
  fallback = DEFAULT_AUTHENTICATED_ROUTE,
  origin?: string,
): InternalRouteTarget {
  const resolved = resolveInternalRedirectTarget(target, fallback, origin);
  const parsed = new URL(resolved, getOrigin(origin));
  const search: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    search[key] = value;
  });

  return {
    pathname: parsed.pathname || fallback,
    search,
    hash: parsed.hash ? parsed.hash.slice(1) : undefined,
  };
}

export function getMissingTenantRedirect(pathname?: string | null) {
  const normalized = normalizePathname(pathname).toLowerCase();
  if (normalized === "/" || normalized === DEFAULT_AUTHENTICATED_ROUTE)
    return null;
  if (isAuthRoutePath(normalized)) return null;
  return DEFAULT_AUTHENTICATED_ROUTE;
}

export function isMissingTenantContextError(error: unknown) {
  const maybeError = error as any;
  const code =
    typeof maybeError?.code === "string"
      ? maybeError.code.trim().toUpperCase()
      : "";
  if (code === "TENANT_MISSING" || code === "TENANT_CONTEXT_MISSING")
    return true;

  const message =
    typeof maybeError?.message === "string"
      ? maybeError.message.trim().toLowerCase()
      : "";
  return (
    message.includes("tenant id not found") ||
    message.includes("tenant context") ||
    message.includes("store context") ||
    message.includes("could not determine your store")
  );
}

export function isRouteChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

export function getRecoverableRouteErrorAction(
  error: unknown,
  location?: RouterLocationLike | null,
) {
  if (isUnauthorizedError(error)) {
    return {
      type: "login" as const,
      search: createLoginRedirectSearch(location, "session-expired"),
    };
  }

  if (isMissingTenantContextError(error)) {
    const to = getMissingTenantRedirect(location?.pathname);
    return to ? { type: "tenant" as const, to } : null;
  }

  return null;
}
